import { PhoneNumberTextField } from '@montugroup/design-system';
import { FormControl } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import type { PhoneNumberType } from '@/pages/components/form-fields/FormType';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { formatPhoneNumberToInternational } from '@/utils/phone-number';

const PhoneNumber = ({
  name,
  autofill,
  inputProps,
  requiredText,
  invalidNumberText,
  mustNotMatch,
  ...rest
}: PhoneNumberType) => {
  const { control, setValue, trigger, getValues } = useFormContext();

  return (
    <FormControl sx={{ my: 2 }} fullWidth>
      <Controller
        rules={{
          required: requiredText,
          validate: (value: string | undefined) => {
            if (!value || !isValidPhoneNumber(value)) {
              return invalidNumberText;
            }

            if (mustNotMatch) {
              const [otherField, message] = mustNotMatch;
              const other = getValues(otherField);

              // Compared in international form, so the same number entered two
              // different ways still counts as a match.
              if (other && formatPhoneNumberToInternational(other) === formatPhoneNumberToInternational(value)) {
                return message;
              }
            }

            return true;
          },
        }}
        control={control}
        defaultValue={null}
        name={name}
        render={({ field: { onChange, value }, fieldState: { error, invalid, isDirty } }) => {
          return (
            <PhoneNumberTextField
              {...rest}
              control={control}
              error={!!error}
              helperText={error?.message}
              name={name}
              onChange={(val: string | undefined) => {
                onChange(val);
                trigger(name);
                // The other field's error depends on this one, so it has to be
                // re-checked when this changes.
                if (mustNotMatch) {
                  trigger(mustNotMatch[0]);
                }
              }}
              value={value}
              sx={{ color: isDirty && !invalid ? 'success' : 'primary' }}
              inputProps={{
                ...inputProps,
                'data-autofill': autofill,
              }}
              onMouseDown={(e: React.MouseEvent<Element, MouseEvent>) => {
                const target = e.target as HTMLInputElement;
                if (e.altKey && target?.dataset?.autofill) {
                  setValue(name, target?.dataset?.autofill);
                  trigger(name);
                }
              }}
            />
          );
        }}
      />
    </FormControl>
  );
};

export default PhoneNumber;
